import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import common from './locales/en/common.json';
import navigation from './locales/en/navigation.json';
import games from './locales/en/games.json';
import lessons from './locales/en/lessons.json';
import settings from './locales/en/settings.json';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '../config.js';

/**
 * Initialize i18next once on module import.
 *
 * Phase 2 (spec §8.2 + §4.1): the EN bundle is loaded synchronously (zero
 * round-trip for the default locale — both prod and vitest rely on this).
 * FR and ES are lazy-loaded via `i18next-http-backend` from
 * `/locales/{lng}/{ns}.json`. Files live in `apps/app/public/locales/`,
 * mirrored from `src/i18n/locales/` — `pnpm i18n:check` fails the build if
 * that mirror drifts, because a stale mirror degrades silently to English.
 *
 * `i18next-browser-languagedetector` caches the user preference in
 * `localStorage["s4k-locale"]`. Detection order: localStorage → URL
 * `?locale=fr` query param → `navigator.language` → fallbackLng (`en`).
 *
 * SUPPORTED_LOCALES = ['en', 'fr', 'es'] when LOCALE_ENABLED; ['en']
 * otherwise.
 *
 * HttpBackend skips any locale already present in `resources`, so bundling
 * EN keeps tests fast (no fetch in vitest) while FR and ES still load via
 * HTTP in prod and audits.
 */
void i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    supportedLngs: [...SUPPORTED_LOCALES],
    fallbackLng: DEFAULT_LOCALE,
    ns: ['common', 'navigation', 'games', 'lessons', 'settings'],
    defaultNS: 'common',
    resources: {
      en: { common, navigation, games, lessons, settings },
    },
    // Without this, i18next treats `resources` as the COMPLETE resource store
    // and never calls the backend — so FR/ES resolved straight back to `en`
    // and the lazy-load path below was decorative. The default is `false`,
    // which is correct only when every locale is bundled. We bundle EN alone
    // (fast tests, no round-trip for the default locale) and fetch the rest,
    // which is exactly what this flag is for.
    partialBundledLanguages: true,
    backend: { loadPath: '/locales/{{lng}}/{{ns}}.json' },
    detection: {
      order: ['localStorage', 'querystring', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 's4k-locale',
      lookupQuerystring: 'locale',
    },
    interpolation: { escapeValue: false }, // React already escapes
    returnEmptyString: false,
  });

export default i18n;